type Eip1193Provider = {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  isMetaMask?: boolean;
  isPhantom?: boolean;
  isBraveWallet?: boolean;
  isBitKeep?: boolean;
  isTokenPocket?: boolean;
  isImToken?: boolean;
  isTrust?: boolean;
  isCoinbaseWallet?: boolean;
  isOkxWallet?: boolean;
  isBybit?: boolean;
  providers?: Eip1193Provider[];
};

type Eip6963ProviderInfo = {
  uuid: string;
  name: string;
  icon: string;
  rdns: string;
};

type Eip6963ProviderDetail = {
  info: Eip6963ProviderInfo;
  provider: Eip1193Provider;
};

type Eip6963AnnounceProviderEvent = CustomEvent<Eip6963ProviderDetail>;

/**
 * Discover injected wallet providers using EIP-6963.
 *
 * Many wallets (Phantom, Bitget, TokenPocket, Brave, Coinbase, OKX, etc.)
 * inject themselves into `window.ethereum` and may even fake `isMetaMask`.
 * The only reliable way to find the real MetaMask (or any specific wallet)
 * is to listen for the EIP-6963 `announceProvider` event and match by `rdns`.
 *
 * The dispatch / announce sequence is synchronous per spec, so by the time
 * this function returns we already collected every wallet that responds.
 */
export const discoverEip6963Providers = (): Eip6963ProviderDetail[] => {
  if (typeof window === 'undefined') return [];

  const providers: Eip6963ProviderDetail[] = [];
  const handler = (event: Event) => {
    const detail = (event as Eip6963AnnounceProviderEvent).detail;
    if (detail?.info?.rdns) {
      providers.push(detail);
    }
  };

  window.addEventListener('eip6963:announceProvider', handler as EventListener);
  window.dispatchEvent(new Event('eip6963:requestProvider'));
  window.removeEventListener(
    'eip6963:announceProvider',
    handler as EventListener,
  );

  return providers;
};

const IMPOSTOR_FLAGS: Array<keyof Eip1193Provider> = [
  'isPhantom',
  'isBraveWallet',
  'isBitKeep',
  'isTokenPocket',
  'isImToken',
  'isTrust',
  'isCoinbaseWallet',
  'isOkxWallet',
  'isBybit',
];

const isImpostor = (provider: Eip1193Provider | undefined) => {
  if (!provider) return false;
  
return IMPOSTOR_FLAGS.some((flag) => Boolean(provider[flag]));
};

/**
 * Find the real MetaMask provider, resilient to other wallets that hijack
 * `window.ethereum` or set `isMetaMask: true` to impersonate MetaMask.
 *
 * Lookup order:
 * 1. EIP-6963 announced provider with `rdns === 'io.metamask'` (most reliable)
 * 2. `window.ethereum.providers` array entry that is MetaMask and not an impostor
 * 3. `window.ethereum` itself, if it really is MetaMask and not an impostor
 */
export const findMetaMaskProvider = (): Eip1193Provider | undefined => {
  if (typeof window === 'undefined') return undefined;

  const announced = discoverEip6963Providers();
  const fromEip6963 = announced.find((p) => p.info.rdns === 'io.metamask');
  if (fromEip6963) return fromEip6963.provider;

  const ethereum = (window as unknown as { ethereum?: Eip1193Provider })
    .ethereum;
  if (!ethereum) return undefined;

  if (Array.isArray(ethereum.providers)) {
    const real = ethereum.providers.find(
      (p) => p?.isMetaMask && !isImpostor(p),
    );
    if (real) return real;
  }

  if (ethereum.isMetaMask && !isImpostor(ethereum)) {
    return ethereum;
  }

  return undefined;
};

export const isMetaMaskInstalled = (): boolean =>
  Boolean(findMetaMaskProvider());
